import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useMatch, useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { getCustomerPortalSession } from "../../constants/auth";
import { getCustomerPortalSettings, isCustomerPortalSettingEnabled } from "utils/customerPortalSettings";
import { fetchCustomerPortalAppointments } from "../../services/customerPortalAppointments.service";
import {
  fetchCustomerPortalAvailabilityDaySlots,
  fetchCustomerPortalAvailabilityMonth,
  fetchCustomerPortalCategories,
  validateCustomerPortalStartTime,
} from "../../services/customerPortalBooking.service";
import {
  clearCustomerPortalBookingServices,
  ensureCustomerPortalCategoryServices,
  selectCustomerPortalBookingServicesMap,
  selectCustomerPortalCategoryServicesLoading,
} from "../../store/customerPortalBookingSlice";
import { storeCustomerPortalBooking } from "../../services/customerPortalBookingCheckout.service";
import BookingWaitlistModal from "pages/online-booking/components/BookingWaitlistModal";
import "assets/css/bookingNew.css";
import "../../styles/customerPortal.css";
import {
  buildBookingApiLines,
  buildPerServiceSchedule,
  buildStartDatetime,
  parseAppointmentOccurrenceMoment,
  computeBookingTotalMinutes,
  computeBookingDisplayMinutes,
  formatDurationHuman,
  isNoPreferenceProvider,
  lowestProviderPrice,
  NO_PREFERENCE_PROVIDER_ID,
  NO_PREFERENCE_PROVIDER_NAME,
  resolveBookingStartMoment,
  validateBeforeSave,
} from "../../utils/bookingPayload";
import { formatPortalDate } from "../../utils/dates";

function formatMoney(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

function norm(s) {
  return String(s || "")
    .trim()
    .toLowerCase();
}

function categoryBadgeCount(cat) {
  if (cat == null) return 0;
  return typeof cat.services_count === "number" ? cat.services_count : 0;
}

/** Salon-local calendar date (YYYY-MM-DD). Waitlist join uses this, not the browser clock. */
function salonTodayYmd(timezone) {
  const tz = String(timezone || "").trim();
  if (!tz) return moment().format("YYYY-MM-DD");
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  } catch (e) {
    return moment().format("YYYY-MM-DD");
  }
}

/** Waitlist is offered only on dates after salon-today, never for the current salon day. */
function isFutureWaitlistDate(value, timezone) {
  const m = moment(value, ["YYYY-MM-DD"], true);
  const day = m.isValid() ? m : moment(value);
  if (!day.isValid()) return false;
  return day.format("YYYY-MM-DD") > salonTodayYmd(timezone);
}

function calendarDayStatus(availMeta) {
  if (!availMeta) return null;
  if (availMeta.status) return availMeta.status;
  return availMeta.available ? "available" : "unavailable";
}

/** Staff off for the whole day — join waitlist. Salon closed days are not waitlist. */
function isFullDayWaitlistStatus(status) {
  return status === "unavailable";
}

/** Yellow calendar days: staff off, or staff working but every slot taken. */
function isWaitlistCalendarStatus(status) {
  return isFullDayWaitlistStatus(status) || status === "fully_booked";
}

function isSalonClosedStatus(status) {
  return status === "closed";
}

function coerceToArray(value) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
}

function isSlotAvailableFlag(raw) {
  return raw === true || raw === 1 || raw === "1" || raw === "true";
}

function normalizeDaySlotEntry(slot) {
  if (slot && typeof slot === "object") {
    return {
      label: String(slot.label || slot.start_time || ""),
      available: isSlotAvailableFlag(slot.available),
    };
  }
  return { label: String(slot || ""), available: true };
}

function unwrapDaySlotsPayload(res) {
  const layers = [];
  if (res && typeof res === "object") {
    layers.push(res);
    if (res.data && typeof res.data === "object" && !Array.isArray(res.data)) {
      layers.push(res.data);
    }
    if (res.data?.data && typeof res.data.data === "object" && !Array.isArray(res.data.data)) {
      layers.push(res.data.data);
    }
  }
  for (const layer of layers) {
    if (
      coerceToArray(layer.day_slots).length > 0 ||
      coerceToArray(layer.waitlist_slots).length > 0 ||
      coerceToArray(layer.slots).length > 0
    ) {
      return layer;
    }
  }
  return layers[layers.length - 1] || {};
}

function labelsFromWaitlistField(value) {
  return coerceToArray(value)
    .map((item) => (typeof item === "string" ? item : String(item?.label || item?.start_time || "")))
    .filter(Boolean);
}

/** Prefer day_slots / waitlist_slots; never drop taken times just because `slots` is shorter. */
function parseDaySlotsResponse(res) {
  const payload = unwrapDaySlotsPayload(res);

  const fromGrid = coerceToArray(payload.day_slots)
    .map(normalizeDaySlotEntry)
    .filter((slot) => slot.label);
  if (fromGrid.length > 0) return fromGrid;

  const available = coerceToArray(payload.slots)
    .map((slot) => normalizeDaySlotEntry(typeof slot === "object" ? slot : { label: slot, available: true }))
    .filter((slot) => slot.label);
  const waitlist = labelsFromWaitlistField(payload.waitlist_slots).map((label) => ({
    label,
    available: false,
  }));
  if (waitlist.length === 0) return available;

  const availableLabels = new Set(available.map((slot) => slot.label));
  return [...waitlist.filter((slot) => !availableLabels.has(slot.label)), ...available];
}

function collectWaitlistLabels(res, parsedSlots) {
  const payload = unwrapDaySlotsPayload(res);
  const fromParsed = (parsedSlots || [])
    .filter((slot) => slot && slot.available !== true)
    .map((slot) => String(slot.label || ""))
    .filter(Boolean);
  return [...new Set([...labelsFromWaitlistField(payload.waitlist_slots), ...fromParsed])];
}

function formatSlotRangeFromLabel(label, durationMinutes) {
  const start = moment(label, ["h:mm A", "hh:mm A", "HH:mm:ss", "HH:mm"], true);
  if (!start.isValid()) return String(label || "");
  const minutes = Math.max(0, Math.round(Number(durationMinutes) || 0));
  if (minutes <= 0) return start.format("h:mm A");
  return `${start.format("h:mm A")} - ${start.clone().add(minutes, "minutes").format("h:mm A")}`;
}

function staffMatchesProvider(staffName, providerName) {
  const a = norm(staffName).replace(/\s+/g, " ");
  const b = norm(providerName).replace(/\s+/g, " ");
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
}

/** Parse portal appointment date into a moment (salon-local). */
function parseAppointmentDateMoment(appt) {
  const occurrence = parseAppointmentOccurrenceMoment(appt);
  return occurrence?.isValid() ? occurrence.clone().startOf("day") : null;
}

/** Start time label from `time_range` (e.g. "9:00 AM-10:30 AM" → "9:00 AM"). */
function parseAppointmentStartSlot(appt) {
  const timeRange = String(appt?.time_range || "").trim();
  if (!timeRange) return null;
  const start = timeRange.includes("-") ? timeRange.split("-")[0].trim() : timeRange;
  return start || null;
}

/** Human-readable "on Thursday 12 December 2024 at 10:00 AM" from portal row fields */
function formatAppointmentWhenHuman(appt) {
  const occurrence = parseAppointmentOccurrenceMoment(appt);
  if (occurrence?.isValid()) {
    return `on ${occurrence.format("dddd D MMMM YYYY")} at ${occurrence.format("h:mm A")}`;
  }

  const dateStr = appt?.date;
  const timeRange = String(appt?.time_range || "").trim();
  const atTime = timeRange.includes("-") ? timeRange.split("-")[0].trim() : timeRange || "—";
  if (!dateStr) {
    return atTime !== "—" ? `at ${atTime}` : "";
  }
  return `on ${formatPortalDate(dateStr)} at ${atTime}`;
}

function buildCalendarCells(monthMoment) {
  const start = monthMoment.clone().startOf("month");
  const daysInMonth = monthMoment.clone().endOf("month").date();
  const pad = start.day();
  const cells = [];
  for (let i = 0; i < pad; i += 1) {
    cells.push({ type: "pad", key: `pad-${i}` });
  }
  for (let d = 1; d <= daysInMonth; d += 1) {
    cells.push({ type: "day", day: d, key: `day-${d}` });
  }
  return cells;
}

function ServiceScheduleIcon({ kind }) {
  if (kind === "follows") {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.75" />
      <path d="M8 3v4M16 3v4M3 10h18" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function EnhancedServiceSummaryItem({ row, index, showRemove, onRemove }) {
  return (
    <li className="cp-services-summary-item cp-services-summary-item--enhanced">
      <span className="cp-services-summary-item-num" aria-hidden>
        {index + 1}
      </span>
      <div className="cp-services-summary-item-body">
        <div className="cp-services-summary-item-main">
          <span className="cp-services-summary-name">{row.serviceName}</span>
          <span className="cp-services-summary-with"> with </span>
          <span className="cp-services-summary-staff">{row.providerName}</span>
          <span className="cp-services-summary-dot" aria-hidden>
            {" "}
            ·{" "}
          </span>
          <span className="cp-services-summary-price">{formatMoney(row.price)}</span>
        </div>
        {row.estimatedDurationLabel ? (
          <p className="cp-services-summary-duration mb-0">
            <span className="cp-services-summary-duration-icon" aria-hidden>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
                <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
              </svg>
            </span>
            <span>Estimated time: {row.estimatedDurationLabel}</span>
          </p>
        ) : null}
        {row.scheduleLabel ? (
          <p
            className={`cp-services-summary-schedule cp-services-summary-schedule--${row.scheduleKind || "start"}`}
          >
            <span className="cp-services-summary-schedule-icon">
              <ServiceScheduleIcon kind={row.scheduleKind} />
            </span>
            <span className="cp-services-summary-schedule-text">{row.scheduleLabel}</span>
          </p>
        ) : null}
      </div>
      {showRemove ? (
        <button
          type="button"
          className="cp-services-summary-remove"
          aria-label={`Remove ${row.serviceName}`}
          onClick={() => onRemove?.(row.serviceId)}
        >
          ×
        </button>
      ) : null}
    </li>
  );
}

/** Segments from API `services[]` or legacy single-service row. */
function appointmentBookingSegments(appointment) {
  if (Array.isArray(appointment?.services) && appointment.services.length > 0) {
    return appointment.services.filter((s) => s?.service_id != null);
  }
  if (appointment?.service_id != null && appointment.service_id !== "") {
    return [
      {
        service_id: Number(appointment.service_id),
        service_name: appointment.service_name,
        staff_id: appointment.staff_id != null ? Number(appointment.staff_id) : null,
        staff_name: appointment.staff_name,
        duration: appointment.duration,
        padding_time: appointment.padding_time,
      },
    ];
  }
  return [];
}

function findServiceInCache(serviceId, categoryRows, servicesByCategoryId) {
  for (const cat of categoryRows) {
    const list = servicesByCategoryId[cat.id];
    if (!Array.isArray(list)) continue;
    const svc = list.find((s) => s.id == serviceId);
    if (svc) return { service: svc, categoryId: cat.id };
  }
  return null;
}

function allSegmentServicesInCache(segmentServiceIds, categoryRows, servicesByCategoryId) {
  if (!segmentServiceIds.length) return false;
  return segmentServiceIds.every((sid) => findServiceInCache(sid, categoryRows, servicesByCategoryId));
}

/** Stable key for comparing booked services (id + staff), order-independent. */
function bookingServiceSignature(lines) {
  if (!Array.isArray(lines) || lines.length === 0) return "";
  return lines
    .map((row) => {
      const sid = row.service_id ?? row.serviceId;
      if (row.noPreference || row.no_preference || isNoPreferenceProvider(row.staff_id ?? row.providerId)) {
        return `${sid}:no_preference`;
      }
      const pid = row.staff_id ?? row.providerId;
      return `${sid}:${pid}`;
    })
    .filter((s) => !s.startsWith(":") && !s.endsWith(":"))
    .sort()
    .join("|");
}

function serviceNameHints(appointment) {
  const segments = appointmentBookingSegments(appointment);
  if (segments.length > 0) {
    return segments.map((s) => norm(s.service_name)).filter(Boolean);
  }
  const combined = String(appointment?.service_name || "").trim();
  if (!combined) return [];
  return combined
    .split(",")
    .map((part) => norm(part))
    .filter(Boolean);
}

function findInitialSelection(appointment, categoryRows, servicesByCategoryId) {
  const rows = Array.isArray(categoryRows) ? categoryRows : [];
  const cache = servicesByCategoryId && typeof servicesByCategoryId === "object" ? servicesByCategoryId : {};
  const staffHint = appointment?.staff_name || "";
  const hints = serviceNameHints(appointment);

  for (const serviceHint of hints) {
    for (const cat of rows) {
      const services = cache[cat.id];
      if (!Array.isArray(services)) continue;
      for (const svc of services) {
        if (
          norm(svc.name) === serviceHint ||
          norm(svc.name).includes(serviceHint) ||
          serviceHint.includes(norm(svc.name))
        ) {
          let providerId = null;
          if (staffHint) {
            for (const p of svc.providers || []) {
              if (staffMatchesProvider(staffHint, p.name)) {
                providerId = p.id;
                break;
              }
            }
          }
          return {
            categoryId: cat.id,
            expandedServiceId: svc.id,
            selectedProviderId: providerId,
          };
        }
      }
    }
  }

  const firstCat = rows[0];
  return {
    categoryId: firstCat?.id ?? null,
    expandedServiceId: null,
    selectedProviderId: null,
  };
}

const CustomerPortalEditAppointment = () => {
  const navigate = useNavigate();
  const { appointmentId } = useParams();
  const location = useLocation();
  const isBookMode = Boolean(useMatch({ path: "/appointments/new", end: true }));
  const isRescheduleMode = Boolean(
    useMatch({ path: "/appointments/:appointmentId/reschedule", end: true })
  );
  const editAppointmentIdForApi =
    !isBookMode && appointmentId && appointmentId !== "new" ? Number(appointmentId) : null;

  useEffect(() => {
    if (isBookMode) return;
    const settings = getCustomerPortalSettings(getCustomerPortalSession()?.user?.customer_portal_settings);
    if (isRescheduleMode && !settings.allow_client_reschedule) {
      navigate("/appointments", { replace: true });
      return;
    }
    if (!isRescheduleMode && !settings.allow_client_edit) {
      navigate("/appointments", { replace: true });
    }
  }, [isBookMode, isRescheduleMode, navigate]);

  const [appointmentRecord, setAppointmentRecord] = useState(() =>
    isBookMode ? null : location.state?.appointment ?? null
  );
  const appointment = appointmentRecord;

  const dispatch = useDispatch();
  const servicesByCategoryIdRaw = useSelector(selectCustomerPortalBookingServicesMap);

  const [step, setStep] = useState(1);
  const [apiCategories, setApiCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const [expandedServiceId, setExpandedServiceId] = useState(null);
  const [selectedProviderByService, setSelectedProviderByService] = useState({});
  /** Ordered unique service ids included in the booking summary (any category). */
  const [bookingServiceIds, setBookingServiceIds] = useState([]);
  const [timeChoice, setTimeChoice] = useState("keep");
  const [noteOpen, setNoteOpen] = useState(false);
  const [appointmentNote, setAppointmentNote] = useState("");
  const [calendarMonth, setCalendarMonth] = useState(() => moment().startOf("month"));
  const [pickedDateStr, setPickedDateStr] = useState(() => moment().format("YYYY-MM-DD"));
  const [pickedSlot, setPickedSlot] = useState(null);
  const [monthAvailDays, setMonthAvailDays] = useState(null);
  const [monthAvailLoading, setMonthAvailLoading] = useState(false);
  const [monthAvailError, setMonthAvailError] = useState(null);
  const [bookingPolicyMinutes, setBookingPolicyMinutes] = useState(0);
  const [daySlots, setDaySlots] = useState([]);
  const [waitlistSlotLabels, setWaitlistSlotLabels] = useState([]);
  const [daySlotsLoading, setDaySlotsLoading] = useState(false);
  const [daySlotsError, setDaySlotsError] = useState(null);
  const [keepTimeValid, setKeepTimeValid] = useState(true);
  const [keepTimeChecking, setKeepTimeChecking] = useState(false);
  const [keepTimeError, setKeepTimeError] = useState(null);
  const [newTimeValid, setNewTimeValid] = useState(true);
  const [newTimeChecking, setNewTimeChecking] = useState(false);
  const [newTimeError, setNewTimeError] = useState(null);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [waitlistContext, setWaitlistContext] = useState(null);

  useEffect(() => {
    dispatch(clearCustomerPortalBookingServices());
  }, [dispatch, editAppointmentIdForApi]);

  const portalSession = getCustomerPortalSession();
  const portalSettings = getCustomerPortalSettings(portalSession?.user?.customer_portal_settings);
  // Slot waitlist should follow the waitlist setting even if a stale session has module_enabled off.
  const waitlistEnabled = portalSettings.waitlist_enabled === true;
  const salonTimezone = portalSession?.user?.salon_timezone || "";
  const waitlistUiEnabled = waitlistEnabled && isFutureWaitlistDate(pickedDateStr, salonTimezone);
  const socialLoginEnabled = isCustomerPortalSettingEnabled(
    portalSession?.user?.customer_portal_settings,
    "social_login_enabled"
  );
  const portalSalonId = portalSession?.user?.salon_id != null
    ? Number(portalSession.user.salon_id)
    : undefined;

  const displayCategories = useMemo(
    () => apiCategories.filter((c) => categoryBadgeCount(c) > 0),
    [apiCategories]
  );

  const servicesCache = useMemo(() => {
    const raw = servicesByCategoryIdRaw || {};
    const out = {};
    Object.entries(raw).forEach(([k, v]) => {
      out[k] = v;
      const n = Number(k);
      if (!Number.isNaN(n)) out[n] = v;
    });
    return out;
  }, [servicesByCategoryIdRaw]);

  const categoryServicesLoading = useSelector((state) =>
    selectCustomerPortalCategoryServicesLoading(state, activeCategoryId)
  );

  const editAppointmentHydratedRef = useRef(false);
  /** Snapshot when edit loads — used to detect service add/remove vs note-only saves. */
  const originalEditSnapshotRef = useRef(null);
  const rescheduleCalendarInitRef = useRef(false);
  /** Prevents background category preload from restarting on every cache update. */
  const categoryPreloadStartedRef = useRef(false);
  /** User changed services/providers after hydrate — do not overwrite from API refresh. */
  const userEditedBookingRef = useRef(false);
  /** Dedupe validate-start-time for identical payloads. */
  const keepTimeValidateSigRef = useRef("");
  const newTimeValidateSigRef = useRef("");
  const keepTimeValidateTimerRef = useRef(null);
  const newTimeValidateTimerRef = useRef(null);

  const initializePickerFromAppointment = useCallback(() => {
    const dateMoment = parseAppointmentDateMoment(appointment);
    if (dateMoment?.isValid()) {
      setCalendarMonth(dateMoment.clone().startOf("month"));
      setPickedDateStr(dateMoment.format("YYYY-MM-DD"));
    }
    const slot = parseAppointmentStartSlot(appointment);
    if (slot) setPickedSlot(slot);
  }, [appointment]);

  const servicesInCategory = useMemo(() => {
    if (activeCategoryId == null) return [];
    const list = servicesCache[activeCategoryId];
    return Array.isArray(list) ? list : [];
  }, [servicesCache, activeCategoryId]);

  const serviceByIdFromCache = useCallback(
    (serviceId) => {
      if (serviceId == null) return null;
      for (const list of Object.values(servicesCache)) {
        if (!Array.isArray(list)) continue;
        const hit = list.find((s) => s.id == serviceId);
        if (hit) return hit;
      }
      return null;
    },
    [servicesCache]
  );

  const activeCategoryListReady =
    activeCategoryId != null && servicesCache[activeCategoryId] !== undefined;

  const servicesPanelLoading =
    activeCategoryId != null && (!activeCategoryListReady || categoryServicesLoading);

  useEffect(() => {
    const session = getCustomerPortalSession();
    if (!session?.token) {
      setApiCategories([]);
      return undefined;
    }
    let cancelled = false;
    setCategoriesLoading(true);
    fetchCustomerPortalCategories(session.token, editAppointmentIdForApi)
      .then(({ data }) => {
        const list = Array.isArray(data?.data) ? data.data : [];
        if (!cancelled) setApiCategories(list);
      })
      .catch(() => {
        if (!cancelled) setApiCategories([]);
      })
      .finally(() => {
        if (!cancelled) setCategoriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [editAppointmentIdForApi]);

  useEffect(() => {
    if (!displayCategories.length) return;
    setActiveCategoryId((prev) => {
      if (prev != null && displayCategories.some((c) => c.id === prev)) return prev;
      return displayCategories[0].id;
    });
  }, [displayCategories]);

  /** One request for the selected category first; Redux dedupes cache + in-flight. */
  useEffect(() => {
    const token = getCustomerPortalSession()?.token;
    if (!token || activeCategoryId == null) return undefined;
    dispatch(ensureCustomerPortalCategoryServices({
      token,
      categoryId: activeCategoryId,
      editAppointmentId: editAppointmentIdForApi,
    }));
  }, [dispatch, activeCategoryId, editAppointmentIdForApi]);

  const activeCategoryServicesLoaded =
    activeCategoryId != null && servicesCache[activeCategoryId] !== undefined;

  /** Edit: preload other categories once after the active tab is ready (background). */
  useEffect(() => {
    if (isBookMode || !appointmentId || appointmentId === "new") return undefined;
    if (categoryPreloadStartedRef.current) return undefined;
    const token = getCustomerPortalSession()?.token;
    if (!token || !displayCategories.length || activeCategoryId == null) return undefined;
    if (!activeCategoryServicesLoaded) return undefined;

    categoryPreloadStartedRef.current = true;
    let cancelled = false;

    (async () => {
      for (const cat of displayCategories) {
        if (cancelled) return;
        if (cat.id === activeCategoryId) continue;
        await dispatch(ensureCustomerPortalCategoryServices({
          token,
          categoryId: cat.id,
          editAppointmentId: editAppointmentIdForApi,
        }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isBookMode,
    appointmentId,
    displayCategories,
    dispatch,
    activeCategoryId,
    activeCategoryServicesLoaded,
    editAppointmentIdForApi,
  ]);

  useEffect(() => {
    categoryPreloadStartedRef.current = false;
    userEditedBookingRef.current = false;
    keepTimeValidateSigRef.current = "";
    newTimeValidateSigRef.current = "";
  }, [appointmentId]);

  useEffect(() => {
    if (isBookMode) return;
    setAppointmentRecord(location.state?.appointment ?? null);
    editAppointmentHydratedRef.current = false;
    originalEditSnapshotRef.current = null;
    rescheduleCalendarInitRef.current = false;
  }, [isBookMode, location.state?.appointment, appointmentId, location.key]);

  /** Edit: always refresh row from API (includes all services on the booking). */
  useEffect(() => {
    if (isBookMode || !appointmentId || appointmentId === "new") return undefined;
    const token = getCustomerPortalSession()?.token;
    if (!token) return undefined;
    let cancelled = false;
    fetchCustomerPortalAppointments(token, {
      focus_appointment_id: Number(appointmentId),
      page: 1,
      per_page: 1,
      status: "all",
    })
      .then((res) => {
        if (cancelled) return;
        const row = Array.isArray(res.data?.data) ? res.data.data[0] : null;
        if (row && !userEditedBookingRef.current) {
          editAppointmentHydratedRef.current = false;
          setAppointmentRecord(row);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isBookMode, appointmentId]);

  /** Edit: hydrate all booked services into the checklist. */
  useEffect(() => {
    if (isBookMode || editAppointmentHydratedRef.current) return;
    if (!displayCategories.length) return;

    const segments = appointmentBookingSegments(appointment || {});
    const partialCache = {};
    for (const c of displayCategories) {
      const v = servicesCache[c.id];
      if (v !== undefined) partialCache[c.id] = v;
    }

    if (segments.length > 0) {
      const serviceIds = segments.map((s) => Number(s.service_id)).filter((id) => Number.isFinite(id));
      if (!allSegmentServicesInCache(serviceIds, displayCategories, partialCache)) return;

      const providerMap = {};
      for (const seg of segments) {
        const sid = Number(seg.service_id);
        if (!Number.isFinite(sid)) continue;
        providerMap[sid] =
          seg.staff_id != null && seg.staff_id !== ""
            ? Number(seg.staff_id)
            : NO_PREFERENCE_PROVIDER_ID;
      }
      const firstHit = findServiceInCache(serviceIds[0], displayCategories, partialCache);
      if (firstHit?.categoryId != null && firstHit.categoryId !== activeCategoryId) {
        setActiveCategoryId(firstHit.categoryId);
        return;
      }

      editAppointmentHydratedRef.current = true;
      setExpandedServiceId(null);
      setBookingServiceIds(serviceIds);
      setSelectedProviderByService(providerMap);
      const initialNote = String(appointment?.booking_notes || "").trim();
      setAppointmentNote(initialNote);
      if (initialNote) setNoteOpen(true);
      originalEditSnapshotRef.current = {
        serviceSignature: bookingServiceSignature(segments),
      };
      return;
    }

    if (!appointment?.service_name) return;
    if (activeCategoryId == null) return;

    const init = findInitialSelection(appointment, displayCategories, partialCache);
    if (init.expandedServiceId == null) return;

    if (init.categoryId != null && init.categoryId !== activeCategoryId) {
      setActiveCategoryId(init.categoryId);
      return;
    }

    const list = servicesCache[activeCategoryId];
    if (!Array.isArray(list)) return;

    editAppointmentHydratedRef.current = true;
    setExpandedServiceId(null);
    setBookingServiceIds([init.expandedServiceId]);
    setSelectedProviderByService({
      [init.expandedServiceId]:
        init.selectedProviderId != null ? init.selectedProviderId : NO_PREFERENCE_PROVIDER_ID,
    });
    const initialNote = String(appointment?.booking_notes || "").trim();
    setAppointmentNote(initialNote);
    if (initialNote) setNoteOpen(true);
    const seg = appointmentBookingSegments(appointment || {});
    originalEditSnapshotRef.current = {
      serviceSignature: bookingServiceSignature(
        seg.length
          ? seg
          : [{ service_id: init.expandedServiceId, staff_id: init.selectedProviderId }]
      ),
    };
  }, [
    isBookMode,
    appointment,
    appointmentId,
    activeCategoryId,
    displayCategories,
    servicesCache,
  ]);

  useEffect(() => {
    if (isBookMode) setTimeChoice("new");
    if (isRescheduleMode) setTimeChoice("new");
  }, [isBookMode, isRescheduleMode]);

  /** Reschedule: open review step once services are loaded. */
  useEffect(() => {
    if (!isRescheduleMode || bookingServiceIds.length === 0) return;
    setStep(2);
  }, [isRescheduleMode, bookingServiceIds.length]);

  /** Reschedule: pre-select current appointment date and start time in the picker. */
  useEffect(() => {
    if (!isRescheduleMode || !appointment || rescheduleCalendarInitRef.current) return;
    const dateMoment = parseAppointmentDateMoment(appointment);
    if (!dateMoment?.isValid()) return;
    rescheduleCalendarInitRef.current = true;
    initializePickerFromAppointment();
  }, [isRescheduleMode, appointment, initializePickerFromAppointment]);

  const pageTitle = isBookMode ? "Book Appointment" : isRescheduleMode ? "Reschedule Appointment" : "Edit Appointment";

  const bookingLines = useMemo(() => {
    const lines = [];
    for (const sid of bookingServiceIds) {
      const svc = serviceByIdFromCache(sid);
      if (!svc) continue;
      const providers = Array.isArray(svc.providers) ? svc.providers : [];
      let pid = selectedProviderByService[sid];
      if (pid == null) {
        if (isBookMode) continue;
        pid = NO_PREFERENCE_PROVIDER_ID;
      }

      const depositBookedOnline = Number(svc.deposit_booked_online) === 1;
      const depositBookedPrice = Number(svc.deposit_booked_price) || 0;

      if (isNoPreferenceProvider(pid)) {
        lines.push({
          serviceId: svc.id,
          serviceName: svc.name,
          providerId: NO_PREFERENCE_PROVIDER_ID,
          providerName: NO_PREFERENCE_PROVIDER_NAME,
          noPreference: true,
          price: lowestProviderPrice(providers),
          durationMinutes: Math.max(0, Math.round(Number(svc.duration) || 0)),
          paddingMinutes: Math.max(0, Math.round(Number(svc.padding_time) || 0)),
          depositBookedOnline,
          depositBookedPrice,
        });
        continue;
      }

      const prov = providers.find((p) => p.id == pid);
      if (!prov) {
        const seg = !isBookMode && appointment ? appointmentBookingSegments(appointment).find((s) => s.service_id == sid) : null;
        const fallbackName = seg?.staff_name ? String(seg.staff_name).trim() : "";
        lines.push({
          serviceId: svc.id,
          serviceName: svc.name,
          providerId: pid,
          providerName: fallbackName || NO_PREFERENCE_PROVIDER_NAME,
          noPreference: !fallbackName,
          price: lowestProviderPrice(providers),
          durationMinutes: Math.max(0, Math.round(Number(svc.duration) || 0)),
          paddingMinutes: Math.max(0, Math.round(Number(svc.padding_time) || 0)),
          depositBookedOnline,
          depositBookedPrice,
        });
        continue;
      }
      lines.push({
        serviceId: svc.id,
        serviceName: svc.name,
        providerId: prov.id,
        providerName: prov.name,
        noPreference: false,
        price: prov.price,
        durationMinutes: Math.max(0, Math.round(Number(svc.duration) || 0)),
        paddingMinutes: Math.max(0, Math.round(Number(svc.padding_time) || 0)),
        depositBookedOnline,
        depositBookedPrice,
      });
    }

    /** Edit: use per-segment duration from API when catalog cache is stale */
    if (!isBookMode && appointment && lines.length > 0) {
      const segments = appointmentBookingSegments(appointment);
      const segByServiceId = {};
      for (const seg of segments) {
        if (seg.service_id != null) segByServiceId[seg.service_id] = seg;
      }

      return lines.map((row, index) => {
        const seg = segByServiceId[row.serviceId];
        if (!seg) return row;
        const isLast = index === lines.length - 1;
        const dur = Math.max(0, Math.round(Number(seg.duration) || 0));
        const pad = isLast ? Math.max(0, Math.round(Number(seg.padding_time) || 0)) : 0;
        if (dur <= 0 && pad <= 0) return row;
        return {
          ...row,
          durationMinutes: dur > 0 ? dur : row.durationMinutes,
          paddingMinutes: pad,
        };
      });
    }

    return lines;
  }, [bookingServiceIds, selectedProviderByService, serviceByIdFromCache, isBookMode, appointment]);

  const lineTotal = useMemo(
    () => bookingLines.reduce((sum, row) => sum + (Number(row.price) || 0), 0),
    [bookingLines]
  );

  const bookingTotalMinutes = useMemo(() => computeBookingTotalMinutes(bookingLines), [bookingLines]);

  const bookingDurationLabel = useMemo(
    () => formatDurationHuman(computeBookingDisplayMinutes(bookingLines)),
    [bookingLines]
  );
  const bookingSlotDurationMinutes = useMemo(
    () => computeBookingDisplayMinutes(bookingLines),
    [bookingLines]
  );

  const segmentsPayload = useMemo(
    () =>
      bookingLines.map((row) => ({
        service_id: Number(row.serviceId),
        ...(row.noPreference
          ? { no_preference: true }
          : { staff_id: Number(row.providerId) }),
        duration_minutes: Math.max(0, Math.round(Number(row.durationMinutes) || 0)),
      })),
    [bookingLines]
  );

  const paddingMinutesPayload = useMemo(() => {
    if (!bookingLines.length) return 0;
    const last = bookingLines[bookingLines.length - 1];
    return Math.max(0, Math.round(Number(last.paddingMinutes) || 0));
  }, [bookingLines]);

  /** Stable signature so availability/validation effects do not loop on new array references. */
  const segmentsRequestKey = useMemo(
    () =>
      JSON.stringify({
        segments: segmentsPayload,
        padding: paddingMinutesPayload,
        exclude:
          !isBookMode && appointmentId && appointmentId !== "new" ? Number(appointmentId) : null,
      }),
    [segmentsPayload, paddingMinutesPayload, isBookMode, appointmentId]
  );

  useEffect(() => {
    keepTimeValidateSigRef.current = "";
    newTimeValidateSigRef.current = "";
  }, [segmentsRequestKey]);

  const canFetchAvailability = bookingLines.length > 0 && bookingTotalMinutes > 0;

  const reviewAppointmentHeading = isBookMode
    ? "Your booking appointment"
    : isRescheduleMode
      ? "Reschedule your appointment"
      : "Your updated appointment";

    const displayDaySlots = useMemo(() => {
    if (!Array.isArray(daySlots)) return [];
    const mapped = daySlots.map((slot) => {
      if (slot && typeof slot === "object") {
        const rawAvailable = slot.available;
        const available = rawAvailable === true || rawAvailable === 1 || rawAvailable === "1";
        return {
          label: String(slot.label || slot.start_time || ""),
          available,
        };
      }
      return { label: String(slot), available: true };
    }).filter((slot) => slot.label);
    const existing = new Set(mapped.map((slot) => slot.label));
    (waitlistSlotLabels || []).forEach((label) => {
      const text = String(label || "");
      if (text && !existing.has(text)) {
        mapped.push({ label: text, available: false });
        existing.add(text);
      }
    });

    return mapped.sort((a, b) => {
      const ta = moment(a.label, ["h:mm A", "hh:mm A", "HH:mm:ss", "HH:mm"], true);
      const tb = moment(b.label, ["h:mm A", "hh:mm A", "HH:mm:ss", "HH:mm"], true);
      if (!ta.isValid() || !tb.isValid()) return a.label.localeCompare(b.label);
      return ta.minutes() + ta.hours() * 60 - (tb.minutes() + tb.hours() * 60);
    });
  }, [daySlots, waitlistSlotLabels]);

  const displayWaitlistSlots = useMemo(() => {
    const labels = [...new Set((waitlistSlotLabels || []).map((label) => String(label || "")).filter(Boolean))];
    const fromGrid = displayDaySlots.filter((slot) => !slot.available).map((slot) => slot.label);
    const merged = [...new Set([...labels, ...fromGrid])];
    return merged.sort((a, b) => {
      const ta = moment(a, ["h:mm A", "hh:mm A", "HH:mm:ss", "HH:mm"], true);
      const tb = moment(b, ["h:mm A", "hh:mm A", "HH:mm:ss", "HH:mm"], true);
      if (!ta.isValid() || !tb.isValid()) return a.localeCompare(b);
      return ta.minutes() + ta.hours() * 60 - (tb.minutes() + tb.hours() * 60);
    });
  }, [waitlistSlotLabels, displayDaySlots]);

  const displayBookableSlots = useMemo(
    () => displayDaySlots.filter((slot) => slot.available === true),
    [displayDaySlots]
  );

  const openWaitlistForDate = useCallback((dateStr, options = {}) => {
    const first = bookingLines[0];
    if (!first?.serviceId || !dateStr || !isFutureWaitlistDate(dateStr, salonTimezone)) return;
    const isFullDay = options.isFullDay !== false && !options.prefilledTime;
    const preferredMoment = moment(dateStr, "YYYY-MM-DD");
    const monthSource = preferredMoment.isValid()
      ? preferredMoment
      : (calendarMonth?.clone?.() || moment());

    setWaitlistContext({
      serviceId: Number(first.serviceId),
      staffId: first.noPreference ? null : (first.providerId != null ? Number(first.providerId) : null),
      serviceName: bookingLines.map((row) => row.serviceName).filter(Boolean).join(" + ") || first.serviceName,
      serviceNames: bookingLines.map((row) => row.serviceName).filter(Boolean),
      services: bookingLines.map((row) => ({
        service_id: Number(row.serviceId),
        staff_id: row.noPreference ? null : (row.providerId != null ? Number(row.providerId) : null),
        name: row.serviceName,
        staff_name: row.providerName || (row.noPreference ? "No Preference" : ""),
        duration_minutes: Math.max(0, Math.round(Number(row.durationMinutes) || 0)),
        duration_label: formatDurationHuman(row.durationMinutes),
      })),
      staffName: first.providerName,
      preferredDate: dateStr,
      dateLabel: preferredMoment.isValid()
        ? preferredMoment.format("ddd D MMM")
        : moment(dateStr, "YYYY-MM-DD").format("ddd D MMM"),
      preferredMonth: monthSource.format("YYYY-MM"),
      monthName: monthSource.format("MMMM"),
      monthLabel: monthSource.format("MMMM YYYY"),
      prefilledTime: options.prefilledTime || null,
      isFullDay,
      replaceAppointmentId: !isBookMode && appointmentId && appointmentId !== "new"
        ? Number(appointmentId)
        : null,
      bookingType: isRescheduleMode || !isBookMode ? "reschedule" : "portal",
      durationMinutes: computeBookingDisplayMinutes(bookingLines),
      timezone: salonTimezone || undefined,
    });
    setShowWaitlistModal(true);
  }, [appointmentId, bookingLines, calendarMonth, isBookMode, isRescheduleMode, salonTimezone]);

  const subtitle = useMemo(() => {
    if (!isBookMode) return "";
    if (bookingLines.length === 0) return "Select services and staff";
    if (bookingLines.length === 1) {
      const row = bookingLines[0];
      return `${row.serviceName} with ${row.providerName}`;
    }
    return `${bookingLines.length} services selected`;
  }, [isBookMode, bookingLines]);

  const whenSentence = useMemo(() => formatAppointmentWhenHuman(appointment || {}), [appointment]);

  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (location.state?.bookingComplete) {
      setSaveSuccess(true);
      setStep(2);
    }
    if (location.state?.restoreStep === 2) {
      setStep(2);
    }
    if (location.state?.slotError) {
      setSaveError(location.state.slotError);
    }
  }, [location.state]);

  const savedWhenSentence = useMemo(() => {
    if (timeChoice === "new" && pickedSlot && pickedDateStr) {
      const dayMoment = moment(pickedDateStr, "YYYY-MM-DD", true);
      if (!dayMoment.isValid()) return whenSentence || "";
      return `on ${dayMoment.format("dddd D MMMM YYYY")} at ${pickedSlot}`;
    }
    return whenSentence || "";
  }, [timeChoice, pickedSlot, pickedDateStr, whenSentence]);

  const bookingStartMoment = useMemo(
    () =>
      resolveBookingStartMoment({
        timeChoice,
        appointment,
        calendarMonth,
        pickedDateStr,
        pickedSlot: timeChoice === "new" ? pickedSlot : null,
      }),
    [timeChoice, appointment, calendarMonth, pickedDateStr, pickedSlot]
  );

  const serviceScheduleLines = useMemo(
    () => buildPerServiceSchedule(bookingLines, bookingStartMoment, savedWhenSentence || whenSentence),
    [bookingLines, bookingStartMoment, savedWhenSentence, whenSentence]
  );

  /** After checkout, booking state is lost on navigation — use draft from success redirect */
  const bookingCompleteDraft = location.state?.bookingComplete ? location.state?.draft : null;

  const successLines = useMemo(() => {
    const fromDraft = bookingCompleteDraft?.bookingLines;
    if (Array.isArray(fromDraft) && fromDraft.length > 0) return fromDraft;
    return bookingLines;
  }, [bookingCompleteDraft, bookingLines]);

  const successWhenSentence = useMemo(() => {
    if (bookingCompleteDraft?.savedWhenSentence) return bookingCompleteDraft.savedWhenSentence;
    return savedWhenSentence;
  }, [bookingCompleteDraft, savedWhenSentence]);

  const successScheduleLines = useMemo(
    () => buildPerServiceSchedule(successLines, null, successWhenSentence),
    [successLines, successWhenSentence]
  );

  const successLineTotal = useMemo(() => {
    if (bookingCompleteDraft?.lineTotal != null) return Number(bookingCompleteDraft.lineTotal) || 0;
    return lineTotal;
  }, [bookingCompleteDraft, lineTotal]);

  const successDurationLabel = useMemo(
    () => formatDurationHuman(computeBookingDisplayMinutes(successLines)),
    [successLines]
  );

  const calendarCells = useMemo(() => buildCalendarCells(calendarMonth), [calendarMonth]);

  const pickedDayInViewMonth = useMemo(() => {
    if (!pickedDateStr) return null;
    const m = moment(pickedDateStr, "YYYY-MM-DD", true);
    if (!m.isValid() || !m.isSame(calendarMonth, "month")) return null;
    return m.date();
  }, [pickedDateStr, calendarMonth]);

  const pickedDayStatus = useMemo(() => {
    if (!pickedDayInViewMonth || !Array.isArray(monthAvailDays)) return null;
    const meta = monthAvailDays.find((x) => x.day === pickedDayInViewMonth);
    return calendarDayStatus(meta);
  }, [pickedDayInViewMonth, monthAvailDays]);

  const pickedDayIsFullDayWaitlist =
    waitlistEnabled && isFutureWaitlistDate(pickedDateStr, salonTimezone) && isFullDayWaitlistStatus(pickedDayStatus);

  /** Day-level waitlist CTA (no per-slot busy list) when this date offers waitlist. */
  const showDayWaitlistCta =
    waitlistUiEnabled &&
    (displayWaitlistSlots.length > 0 || pickedDayStatus === "fully_booked");

  const showReviewTimePicker =
    canFetchAvailability && (timeChoice === "new" || isRescheduleMode) && (step === 2 || isRescheduleMode);

  useEffect(() => {
    if (!showReviewTimePicker) {
      setMonthAvailDays(null);
      setMonthAvailError(null);
      setMonthAvailLoading(false);
      return undefined;
    }
    const session = getCustomerPortalSession();
    if (!session?.token) {
      setMonthAvailDays(null);
      setMonthAvailError("Please sign in again.");
      return undefined;
    }
    let cancelled = false;
    setMonthAvailLoading(true);
    setMonthAvailError(null);
    const body = JSON.parse(segmentsRequestKey);
    fetchCustomerPortalAvailabilityMonth(session.token, {
      year_month: calendarMonth.format("YYYY-MM"),
      segments: body.segments,
      padding_minutes: body.padding,
      ...(Number.isFinite(body.exclude) ? { exclude_appointment_id: body.exclude } : {}),
    })
      .then((res) => {
        if (!cancelled) {
          setMonthAvailDays(Array.isArray(res.data?.days) ? res.data.days : []);
          setBookingPolicyMinutes(Number(res.data?.booking_policy_minutes) || 0);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setMonthAvailDays(null);
          setMonthAvailError(err?.response?.data?.message || "Could not load calendar availability.");
        }
      })
      .finally(() => {
        if (!cancelled) setMonthAvailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    showReviewTimePicker,
    calendarMonth,
    segmentsRequestKey,
  ]);

  useEffect(() => {
    if (!showReviewTimePicker) {
      setDaySlots((prev) => (prev.length === 0 ? prev : []));
      setWaitlistSlotLabels((prev) => (prev.length === 0 ? prev : []));
      setDaySlotsError(null);
      setDaySlotsLoading(false);
      return undefined;
    }
    const session = getCustomerPortalSession();
    if (!session?.token) {
      setDaySlots([]);
      setWaitlistSlotLabels([]);
      return undefined;
    }
    const dateStr = pickedDateStr;
    if (!dateStr) {
      setDaySlots([]);
      setWaitlistSlotLabels([]);
      return undefined;
    }
    const pickedStatus = calendarDayStatus(
      Array.isArray(monthAvailDays)
        ? monthAvailDays.find((x) => x.day === pickedDayInViewMonth)
        : null
    );
    if (
      pickedDayInViewMonth &&
      (isSalonClosedStatus(pickedStatus) || isFullDayWaitlistStatus(pickedStatus))
    ) {
      setDaySlots([]);
      setWaitlistSlotLabels([]);
      setDaySlotsLoading(false);
      setDaySlotsError(null);
      return undefined;
    }
    let cancelled = false;
    setDaySlotsLoading(true);
    setDaySlotsError(null);
    const body = JSON.parse(segmentsRequestKey);
    fetchCustomerPortalAvailabilityDaySlots(session.token, {
      date: dateStr,
      segments: body.segments,
      padding_minutes: body.padding,
      ...(Number.isFinite(body.exclude) ? { exclude_appointment_id: body.exclude } : {}),
    })
      .then((res) => {
        if (!cancelled) {
          const parsed = parseDaySlotsResponse(res);
          setDaySlots(parsed);
          setWaitlistSlotLabels(collectWaitlistLabels(res, parsed));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setDaySlots([]);
          setWaitlistSlotLabels([]);
          setDaySlotsError(err?.response?.data?.message || "Could not load times for this day.");
        }
      })
      .finally(() => {
        if (!cancelled) setDaySlotsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    showReviewTimePicker,
    pickedDateStr,
    segmentsRequestKey,
    monthAvailDays,
    pickedDayInViewMonth,
  ]);

  useEffect(() => {
    if (isRescheduleMode || timeChoice === "new") return;
    setPickedSlot((prev) =>
      prev && displayDaySlots.some((slot) => slot.available && slot.label === prev) ? prev : null
    );
  }, [displayDaySlots, isRescheduleMode, timeChoice]);

  useEffect(() => {
    if (step !== 2 || timeChoice !== "keep" || !canFetchAvailability || isBookMode) {
      setKeepTimeValid(true);
      setKeepTimeError(null);
      setKeepTimeChecking(false);
      keepTimeValidateSigRef.current = "";
      return undefined;
    }

    const startDatetime = buildStartDatetime({
      timeChoice: "keep",
      appointment,
      calendarMonth,
      pickedDateStr,
      pickedSlot: null,
    });
    if (!startDatetime) {
      setKeepTimeValid(false);
      setKeepTimeError("Could not read your current appointment time.");
      keepTimeValidateSigRef.current = "";
      return undefined;
    }

    const session = getCustomerPortalSession();
    if (!session?.token) {
      setKeepTimeValid(false);
      setKeepTimeError("Please sign in again.");
      keepTimeValidateSigRef.current = "";
      return undefined;
    }

    const requestKey = JSON.stringify({ startDatetime, segmentsRequestKey });
    if (keepTimeValidateSigRef.current === requestKey) {
      return undefined;
    }

    let cancelled = false;
    setKeepTimeChecking(true);
    setKeepTimeError(null);

    if (keepTimeValidateTimerRef.current) {
      clearTimeout(keepTimeValidateTimerRef.current);
    }

    keepTimeValidateTimerRef.current = setTimeout(() => {
      if (cancelled) return;

      const parsed = JSON.parse(segmentsRequestKey);
      const body = {
        start_datetime: startDatetime,
        segments: parsed.segments,
        padding_minutes: parsed.padding,
      };
      if (Number.isFinite(parsed.exclude)) {
        body.exclude_appointment_id = parsed.exclude;
      }

      validateCustomerPortalStartTime(session.token, body)
        .then((res) => {
          if (cancelled) return;
          keepTimeValidateSigRef.current = requestKey;
          const ok = Boolean(res.data?.available);
          setKeepTimeValid(ok);
          setKeepTimeError(ok ? null : res.data?.message || "This time is no longer available for the selected services.");
        })
        .catch((err) => {
          if (cancelled) return;
          setKeepTimeValid(false);
          setKeepTimeError(err?.response?.data?.message || "Could not verify appointment time.");
        })
        .finally(() => {
          if (!cancelled) setKeepTimeChecking(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      if (keepTimeValidateTimerRef.current) {
        clearTimeout(keepTimeValidateTimerRef.current);
        keepTimeValidateTimerRef.current = null;
      }
    };
  }, [
    step,
    timeChoice,
    canFetchAvailability,
    isBookMode,
    appointment,
    segmentsRequestKey,
  ]);

  useEffect(() => {
    if (
      step !== 2 ||
      !canFetchAvailability ||
      !(timeChoice === "new" || isRescheduleMode) ||
      !pickedSlot
    ) {
      setNewTimeValid(true);
      setNewTimeError(null);
      setNewTimeChecking(false);
      newTimeValidateSigRef.current = "";
      return undefined;
    }

    if (daySlotsLoading) {
      setNewTimeChecking(true);
      return undefined;
    }

    if (!displayDaySlots.some((slot) => slot.available && slot.label === pickedSlot)) {
      setNewTimeValid(false);
      setNewTimeError(
        "This time is not available for the selected staff. Please choose another slot."
      );
      setNewTimeChecking(false);
      newTimeValidateSigRef.current = "";
      return undefined;
    }

    const startDatetime = buildStartDatetime({
      timeChoice: "new",
      appointment,
      calendarMonth,
      pickedDateStr,
      pickedSlot,
    });
    if (!startDatetime) {
      setNewTimeValid(false);
      setNewTimeError("Could not determine appointment time.");
      setNewTimeChecking(false);
      newTimeValidateSigRef.current = "";
      return undefined;
    }

    const session = getCustomerPortalSession();
    if (!session?.token) {
      setNewTimeValid(false);
      setNewTimeError("Please sign in again.");
      setNewTimeChecking(false);
      newTimeValidateSigRef.current = "";
      return undefined;
    }

    const requestKey = JSON.stringify({ startDatetime, segmentsRequestKey, pickedSlot });
    if (newTimeValidateSigRef.current === requestKey) {
      return undefined;
    }

    let cancelled = false;
    setNewTimeChecking(true);
    setNewTimeError(null);

    if (newTimeValidateTimerRef.current) {
      clearTimeout(newTimeValidateTimerRef.current);
    }

    newTimeValidateTimerRef.current = setTimeout(() => {
      if (cancelled) return;

      const parsed = JSON.parse(segmentsRequestKey);
      const body = {
        start_datetime: startDatetime,
        segments: parsed.segments,
        padding_minutes: parsed.padding,
      };
      if (Number.isFinite(parsed.exclude)) {
        body.exclude_appointment_id = parsed.exclude;
      }

      validateCustomerPortalStartTime(session.token, body)
        .then((res) => {
          if (cancelled) return;
          newTimeValidateSigRef.current = requestKey;
          const ok = Boolean(res.data?.available);
          setNewTimeValid(ok);
          setNewTimeError(
            ok ? null : res.data?.message || "This time is not available for the selected services."
          );
        })
        .catch((err) => {
          if (cancelled) return;
          setNewTimeValid(false);
          setNewTimeError(err?.response?.data?.message || "Could not verify appointment time.");
        })
        .finally(() => {
          if (!cancelled) setNewTimeChecking(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      if (newTimeValidateTimerRef.current) {
        clearTimeout(newTimeValidateTimerRef.current);
        newTimeValidateTimerRef.current = null;
      }
    };
  }, [
    step,
    timeChoice,
    isRescheduleMode,
    canFetchAvailability,
    pickedSlot,
    daySlotsLoading,
    displayDaySlots,
    appointment,
    segmentsRequestKey,
    calendarMonth,
    pickedDateStr,
  ]);

  const setProviderForService = useCallback((serviceId, providerId) => {
    userEditedBookingRef.current = true;
    setSelectedProviderByService((prev) => ({ ...prev, [serviceId]: providerId }));
  }, []);

  const handleSaveChanges = useCallback(async () => {
    const startDatetime = buildStartDatetime({
      timeChoice,
      appointment,
      calendarMonth,
      pickedDateStr,
      pickedSlot,
    });
    const validationMsg = validateBeforeSave({
      bookingLines,
      timeChoice,
      pickedSlot,
      isBookMode,
      startDatetime,
    });
    if (validationMsg) {
      setSaveError(validationMsg);
      return;
    }

    if (!isBookMode && timeChoice === "keep" && !keepTimeValid) {
      setSaveError(keepTimeError || "Your original time is not available for the selected services. Please choose a new time.");
      return;
    }

    if ((timeChoice === "new" || isRescheduleMode) && !newTimeValid) {
      setSaveError(newTimeError || "Please choose an available time for the selected staff.");
      return;
    }

    const draft = {
      mode: isBookMode ? "book" : "edit",
      editAppointmentId: !isBookMode && appointmentId ? Number(appointmentId) : null,
      portalIntent: isRescheduleMode ? "reschedule" : "edit",
      bookingLines,
      startDatetime,
      note: appointmentNote,
      lineTotal,
      savedWhenSentence,
      isBookMode,
      isRescheduleMode,
    };

    const currentServiceSignature = bookingServiceSignature(bookingLines);
    const originalServiceSignature = originalEditSnapshotRef.current?.serviceSignature ?? "";
    const servicesChanged =
      !isBookMode &&
      originalServiceSignature !== "" &&
      currentServiceSignature !== originalServiceSignature;

    // New bookings always go through checkout so deposit is decided by the API
    // (avoids skipping card entry when the local catalog cache is stale/missing flags).
    // Edits with service changes also need checkout for any additional deposit due.
    const shouldUseCheckout = isBookMode || (!isRescheduleMode && servicesChanged);

    if (shouldUseCheckout) {
      navigate("/appointments/checkout", { state: { bookingDraft: draft } });
      return;
    }

    const token = getCustomerPortalSession()?.token;
    if (!token) {
      setSaveError("Please sign in again.");
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      await storeCustomerPortalBooking(token, {
        mode: draft.mode,
        edit_appointment_id: draft.editAppointmentId || undefined,
        portal_intent: isRescheduleMode ? "reschedule" : "edit",
        start_datetime: startDatetime,
        note: appointmentNote,
        lines: buildBookingApiLines(bookingLines),
        cancellation_policy_agreed: true,
      });
      setSaveSuccess(true);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.message || e?.message || "Could not save appointment.";
      // Safety net: if backend still requires a deposit card, open checkout instead of dead-ending here.
      if (/card details are required/i.test(String(msg))) {
        navigate("/appointments/checkout", { state: { bookingDraft: draft } });
        return;
      }
      setSaveError(msg);
      if (status === 410) setStep(2);
    } finally {
      setSaving(false);
    }
  }, [
    appointment,
    appointmentId,
    appointmentNote,
    bookingLines,
    calendarMonth,
    isBookMode,
    lineTotal,
    navigate,
    pickedDateStr,
    pickedSlot,
    savedWhenSentence,
    timeChoice,
    keepTimeValid,
    keepTimeError,
    newTimeValid,
    newTimeError,
    isRescheduleMode,
  ]);

  const toggleBookingService = useCallback((serviceId, checked) => {
    userEditedBookingRef.current = true;
    setBookingServiceIds((prev) => {
      if (!checked) return prev.filter((id) => id != serviceId);
      if (prev.some((id) => id == serviceId)) return prev;
      return [...prev, serviceId];
    });
    if (checked) {
      setSelectedProviderByService((prev) => ({ ...prev, [serviceId]: NO_PREFERENCE_PROVIDER_ID }));
      setExpandedServiceId(serviceId);
    } else {
      setExpandedServiceId((prevEx) => (prevEx == serviceId ? null : prevEx));
      setSelectedProviderByService((prev) => {
        const next = { ...prev };
        delete next[serviceId];
        return next;
      });
    }
  }, []);

  const removeBookingService = useCallback((serviceId) => {
    userEditedBookingRef.current = true;
    setBookingServiceIds((prev) => prev.filter((id) => id != serviceId));
    setExpandedServiceId((prevEx) => (prevEx == serviceId ? null : prevEx));
  }, []);

  const toggleExpand = useCallback((serviceId) => {
    setExpandedServiceId((prev) => (prev === serviceId ? null : serviceId));
  }, []);

  const handleCategoryClick = useCallback(
    (catId) => {
      setActiveCategoryId(catId);
      const list = servicesCache[catId];
      const resolved = Array.isArray(list) ? list : [];
      setExpandedServiceId((prev) => {
        if (resolved.length === 0 || prev == null) return null;
        return resolved.some((s) => s.id == prev) ? prev : null;
      });
    },
    [servicesCache]
  );

  const goReview = useCallback(() => {
    if (bookingLines.length === 0) return;
    setStep(2);
  }, [bookingLines.length]);

  if (saveSuccess) {
    return (
      <div className="cp-page cp-edit-appt-page cp-edit-appt-success-page">
        <div className="cp-edit-appt-success-inner">
          <div className="cp-edit-appt-success-icon-wrap" aria-hidden>
            <div className="cp-edit-appt-success-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          <h1 className="cp-edit-appt-success-heading">
            {isBookMode ? "Appointment booked!" : isRescheduleMode ? "Appointment rescheduled!" : "Appointment updated!"}
          </h1>
          <p className="cp-edit-appt-success-lead">
            {isBookMode
              ? "Your booking is confirmed and a confirmation email has been sent."
              : isRescheduleMode
                ? "Your new date and time are confirmed and a confirmation email has been sent."
                : "Your changes have been saved and a confirmation email has been sent."}
          </p>
          <div className="cp-edit-appt-success-card-outer">
            {successLines.length > 0 ? (
              <div className="cp-edit-appt-success-card-inset">
                <p className="cp-edit-appt-success-card-label">Booking summary</p>

                <ul className="cp-edit-appt-success-services list-unstyled">
                  {successScheduleLines.map((row, index) => (
                    <li key={row.serviceId} className="cp-edit-appt-success-service">
                      <span className="cp-edit-appt-success-service-num" aria-hidden>
                        {index + 1}
                      </span>
                      <div className="cp-edit-appt-success-service-body">
                        <div className="cp-edit-appt-success-service-main">
                          <span className="cp-edit-appt-success-service-name">{row.serviceName}</span>
                          <span className="cp-edit-appt-success-service-meta">with {row.providerName}</span>
                          <span className="cp-edit-appt-success-service-price">{formatMoney(row.price)}</span>
                        </div>
                        {row.scheduleLabel ? (
                          <p className="cp-edit-appt-success-service-schedule mb-0">{row.scheduleLabel}</p>
                        ) : null}
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="cp-edit-appt-success-details">
                  {successDurationLabel ? (
                    <div className="cp-edit-appt-success-detail">
                      <span className="cp-edit-appt-success-detail-icon" aria-hidden>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
                          <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                        </svg>
                      </span>
                      <span className="cp-edit-appt-success-detail-text">{successDurationLabel} appointment</span>
                    </div>
                  ) : null}
                </div>

                <div className="cp-edit-appt-success-total" aria-label="Booking total">
                  <span className="cp-edit-appt-success-total-label">Total</span>
                  <span className="cp-edit-appt-success-total-value">{formatMoney(successLineTotal)}</span>
                </div>
              </div>
            ) : null}
          </div>
          <Link to="/appointments" className="cp-edit-appt-success-cta">
            Back to my appointments <i className="fa fa-arrow-right" aria-hidden="true" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="cp-page cp-edit-appt-page">
      <Link to="/appointments" className="cp-edit-appt-back">
        <i className="fa fa-arrow-left" aria-hidden="true" /> Back to My Appointments
      </Link>

      <h5 className={`cp-edit-appt-title mb-1${subtitle ? "" : " mb-3"}`}>{pageTitle}</h5>
      {subtitle ? <p className="cp-edit-appt-subtitle mb-3">{subtitle}</p> : null}

      {isRescheduleMode && bookingLines.length === 0 ? (
        <p className="text-muted small mb-3">Loading your appointment…</p>
      ) : null}

      {!isRescheduleMode ? (
        <div className="cp-edit-appt-stepper mb-4" role="tablist" aria-label={isBookMode ? "Booking steps" : "Edit steps"}>
          <button
            type="button"
            role="tab"
            aria-selected={step === 1}
            className={`cp-edit-appt-step ${step === 1 ? "active" : ""}`}
            onClick={() => setStep(1)}
          >
            1. Services
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={step === 2}
            className={`cp-edit-appt-step ${step === 2 ? "active" : ""}`}
            onClick={() => bookingLines.length > 0 && setStep(2)}
            disabled={bookingLines.length === 0}
          >
            2. Review &amp; save
          </button>
        </div>
      ) : null}

      {isRescheduleMode && bookingLines.length === 0 ? null : !isRescheduleMode && step === 1 ? (
        <div className="cp-edit-appt-grid">
          <aside className="cp-edit-appt-cats" aria-label="Service categories">
            <ul className="cp-edit-appt-cat-list">
              {categoriesLoading && apiCategories.length === 0 ? (
                <li className="text-muted small px-2 py-3">Loading categories…</li>
              ) : (
                displayCategories.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className={`cp-edit-appt-cat-btn ${activeCategoryId === c.id ? "active" : ""}`}
                      onClick={() => handleCategoryClick(c.id)}
                    >
                      <span className="cp-edit-appt-cat-name">{c.name}</span>
                      <span className="cp-edit-appt-cat-badge">{categoryBadgeCount(c)}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </aside>

          <section className="cp-edit-appt-services" aria-label="Services">
            {servicesPanelLoading ? (
              <p className="text-muted small px-2 py-3 mb-0">Loading services…</p>
            ) : servicesInCategory.length === 0 ? (
              <p className="text-muted small px-2 py-3 mb-0">No bookable services in this category.</p>
            ) : (
              servicesInCategory.map((svc) => {
                const providers = Array.isArray(svc.providers) ? svc.providers : [];
                const isOpen = expandedServiceId == svc.id;
                const selectedPid = selectedProviderByService[svc.id];
                const isBooked = bookingServiceIds.some((id) => id == svc.id);

                return (
                  <div key={svc.id} className={`cp-edit-appt-acc ${isOpen ? "open" : ""}`}>
                    <div className="cp-edit-appt-acc-bar">
                      <label className="cp-edit-appt-acc-include">
                        <input
                          type="checkbox"
                          checked={isBooked}
                          onChange={(e) => toggleBookingService(svc.id, e.target.checked)}
                          aria-label={`Include ${svc.name} in your booking`}
                        />
                        <span className="cp-edit-appt-acc-title">{svc.name}</span>
                      </label>
                      <button
                        type="button"
                        className="cp-edit-appt-acc-expand"
                        aria-expanded={isOpen}
                        aria-label={isOpen ? `Collapse ${svc.name}` : `Expand ${svc.name}`}
                        onClick={() => toggleExpand(svc.id)}
                      >
                        <span className="cp-edit-appt-acc-toggle" aria-hidden>
                          {isOpen ? "−" : "+"}
                        </span>
                      </button>
                    </div>
                    {isOpen ? (
                      <div className="cp-edit-appt-acc-body">
                        <p className="cp-edit-appt-acc-desc">{svc.description || ""}</p>
                        <div className="cp-edit-appt-providers" role="radiogroup" aria-label="Choose provider">
                          <label
                            className={`cp-edit-appt-provider ${selectedPid == null || isNoPreferenceProvider(selectedPid) ? "selected" : ""}`}
                          >
                            <input
                              type="radio"
                              name={`provider-${svc.id}`}
                              checked={selectedPid == null || isNoPreferenceProvider(selectedPid)}
                              onChange={() => setProviderForService(svc.id, NO_PREFERENCE_PROVIDER_ID)}
                            />
                            <span className="cp-edit-appt-provider-avatar">NP</span>
                            <span className="cp-edit-appt-provider-name">{NO_PREFERENCE_PROVIDER_NAME}</span>
                            <span className="cp-edit-appt-provider-price">
                              {providers.length > 0 ? formatMoney(lowestProviderPrice(providers)) : ""}
                            </span>
                          </label>
                          {providers.map((p) => {
                            const checked = selectedPid != null && !isNoPreferenceProvider(selectedPid) && selectedPid == p.id;
                            return (
                              <label key={p.id} className={`cp-edit-appt-provider ${checked ? "selected" : ""}`}>
                                <input
                                  type="radio"
                                  name={`provider-${svc.id}`}
                                  checked={checked}
                                  onChange={() => setProviderForService(svc.id, p.id)}
                                />
                                <span className="cp-edit-appt-provider-avatar">{p.initials}</span>
                                <span className="cp-edit-appt-provider-name">{p.name}</span>
                                <span className="cp-edit-appt-provider-price">{formatMoney(p.price)}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </section>

          <aside className="cp-edit-appt-summary" aria-label="Summary">
            <div className="cp-edit-appt-summary-card">
              <div className="cp-services-summary-panel cp-edit-appt-summary-services-panel">
                <div className="cp-services-summary-head">
                  <span className="cp-services-summary-label">Services</span>
                  {bookingLines.length > 0 ? (
                    <span className="cp-services-summary-badge">{bookingLines.length}</span>
                  ) : null}
                </div>

                {bookingLines.length > 0 ? (
                  <ul className="cp-services-summary-list list-unstyled mb-0">
                    {serviceScheduleLines.map((row, index) => (
                      <EnhancedServiceSummaryItem
                        key={row.serviceId}
                        row={row}
                        index={index}
                        showRemove
                        onRemove={removeBookingService}
                      />
                    ))}
                  </ul>
                ) : bookingServiceIds.length > 0 ? (
                  <p className="cp-services-summary-hint mb-0">
                    Selected services appear here. Staff defaults to No Preference — change it anytime before booking.
                  </p>
                ) : (
                  <p className="cp-services-summary-hint mb-0">
                    Tick the checkbox next to a service to add it. No Preference is selected by default.
                  </p>
                )}

                {bookingLines.length > 0 ? (
                  <>
                    <div className="cp-services-summary-foot">
                      <span className="cp-services-summary-total-label">Total:</span>
                      <span className="cp-services-summary-total-value">{formatMoney(lineTotal)}</span>
                    </div>
                    {bookingDurationLabel ? (
                      <div className="cp-services-summary-foot cp-services-summary-foot--secondary">
                        <span className="cp-services-summary-time-label">Estimated time</span>
                        <span className="cp-services-summary-time-value">{bookingDurationLabel}</span>
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>

              <button type="button" className="cp-edit-appt-next-btn" disabled={bookingLines.length === 0} onClick={goReview}>
                <span className="cp-edit-appt-next-btn-label">Next — Review &amp; save</span>
                <span className="cp-edit-appt-next-btn-price">{formatMoney(lineTotal)}</span>
              </button>
            </div>
          </aside>
        </div>
      ) : (
        <div className="cp-edit-appt-review cp-edit-appt-review-v2">
          <div className="cp-edit-appt-review-shell" data-appointment-id={appointmentId}>
            <div className="cp-edit-appt-review-card">
              <h2 className="cp-edit-appt-review-page-title">{reviewAppointmentHeading}</h2>

              {bookingLines.length > 0 ? (
                <>
                  <div className="cp-edit-appt-review-summary-box cp-services-summary-panel">
                    <div className="cp-services-summary-head">
                      <span className="cp-services-summary-label">Services</span>
                      <span className="cp-services-summary-badge">{bookingLines.length}</span>
                    </div>
                    <ul className="cp-services-summary-list list-unstyled mb-0 cp-services-summary-list--review">
                      {serviceScheduleLines.map((row, index) => (
                        <EnhancedServiceSummaryItem key={row.serviceId} row={row} index={index} />
                      ))}
                    </ul>
                    <div className="cp-services-summary-foot">
                      <span className="cp-services-summary-total-label">Total:</span>
                      <span className="cp-services-summary-total-value">{formatMoney(lineTotal)}</span>
                    </div>
                    {bookingDurationLabel ? (
                      <div className="cp-services-summary-foot cp-services-summary-foot--secondary">
                        <span className="cp-services-summary-time-label">Estimated time</span>
                        <span className="cp-services-summary-time-value">{bookingDurationLabel}</span>
                      </div>
                    ) : null}
                  </div>

                  <div className={`cp-edit-appt-review-datetime${isRescheduleMode ? " is-reschedule" : ""}`}>
                    <div className="cp-edit-appt-review-datetime-label">Date &amp; time</div>
                    {isRescheduleMode ? (
                      <p className="cp-edit-appt-review-reschedule-hint text-muted small mb-2">
                        Your current appointment date and time are selected below. Choose another slot if you need to
                        change them.
                      </p>
                    ) : null}
                    <div className="cp-edit-appt-review-datetime-row">
                      <button
                        type="button"
                        className={`cp-edit-appt-review-time-btn cp-edit-appt-review-time-btn--keep ${
                          timeChoice === "keep" ? "is-on" : "is-off"
                        } ${!keepTimeValid && timeChoice === "keep" ? "is-invalid" : ""}`}
                        disabled={isBookMode}
                        onClick={() => setTimeChoice("keep")}
                      >
                        Keep original time
                      </button>
                      <button
                        type="button"
                        className={`cp-edit-appt-review-time-btn cp-edit-appt-review-time-btn--new ${
                          timeChoice === "new" ? "is-on" : "is-off"
                        }`}
                        onClick={() => {
                          if (!isBookMode && appointment) {
                            initializePickerFromAppointment();
                          }
                          setTimeChoice("new");
                        }}
                      >
                        Choose a new time
                      </button>
                    </div>
                    {!isBookMode && !isRescheduleMode && timeChoice === "keep" ? (
                      <div className="cp-edit-appt-review-keep-time-status">
                        {keepTimeChecking ? (
                          <div className="cp-edit-appt-review-keep-time-checking" role="status">
                            <span className="cp-edit-appt-review-keep-time-spinner" aria-hidden />
                            <span>Checking whether your original time fits all selected services…</span>
                          </div>
                        ) : null}
                        {!keepTimeChecking && !keepTimeValid ? (
                          <div className="cp-edit-appt-review-keep-time-alert" role="alert">
                            <span className="cp-edit-appt-review-keep-time-alert-icon" aria-hidden>
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                <path
                                  d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                                  stroke="currentColor"
                                  strokeWidth="1.75"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>
                            <div className="cp-edit-appt-review-keep-time-alert-body">
                              <strong className="cp-edit-appt-review-keep-time-alert-title">
                                Original time is not available
                              </strong>
                              <p className="cp-edit-appt-review-keep-time-alert-message mb-0">
                                {keepTimeError ||
                                  "Your appointment start overlaps staff busy time or another booking before all services can finish."}
                              </p>
                              <p className="cp-edit-appt-review-keep-time-alert-hint mb-0">
                                Select <strong>Choose a new time</strong> and pick a slot that works for every service
                                listed above.
                              </p>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>

                  {timeChoice === "new" || isRescheduleMode ? (
                    <div className="cp-edit-appt-review-picker">
                      {!canFetchAvailability ? (
                        <p className="cp-edit-appt-review-avail-hint text-muted small mb-0">
                          Add services with estimated duration (from your salon catalog) to load real availability for your team.
                        </p>
                      ) : (
                        <div className="cp-edit-appt-review-picker-inner">
                          {monthAvailError ? (
                            <p className="cp-edit-appt-review-avail-hint text-danger small">{monthAvailError}</p>
                          ) : null}
                          <div className="cp-edit-appt-review-cal-wrap">
                            <div className="cp-edit-appt-review-cal-nav">
                              <button
                                type="button"
                                className="cp-edit-appt-review-cal-nav-btn"
                                aria-label="Previous month"
                                onClick={() => setCalendarMonth((m) => m.clone().subtract(1, "month"))}
                              >
                                ‹
                              </button>
                              <span className="cp-edit-appt-review-cal-nav-title">
                                {calendarMonth.format("MMMM YYYY")}
                              </span>
                              <button
                                type="button"
                                className="cp-edit-appt-review-cal-nav-btn"
                                aria-label="Next month"
                                onClick={() => setCalendarMonth((m) => m.clone().add(1, "month"))}
                              >
                                ›
                              </button>
                            </div>
                            <div className="cp-edit-appt-review-cal-dow" aria-hidden>
                              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
                                <div key={d} className="cp-edit-appt-review-cal-dow-cell">
                                  {d}
                                </div>
                              ))}
                            </div>
                            <div className="cp-edit-appt-review-cal-grid">
                              {calendarCells.map((cell) => {
                                if (cell.type === "pad") {
                                  return <div key={cell.key} className="cp-edit-appt-review-cal-pad" />;
                                }
                                const dayMoment = calendarMonth.clone().date(cell.day);
                                const dayDateStr = dayMoment.format("YYYY-MM-DD");
                                const isSel = pickedDateStr === dayDateStr;
                                const salonToday = salonTodayYmd(salonTimezone);
                                const isToday = dayDateStr === salonToday;
                                const isPast = dayDateStr < salonToday;
                                const isFutureDay = dayDateStr > salonToday;
                                const hasMonthData = Array.isArray(monthAvailDays);
                                const availMeta = hasMonthData ? monthAvailDays.find((x) => x.day === cell.day) : null;
                                const dayStatus = calendarDayStatus(availMeta);
                                const isSalonClosed = hasMonthData && isSalonClosedStatus(dayStatus);
                                const isStaffOff = hasMonthData && isFullDayWaitlistStatus(dayStatus);
                                const isFullyBookedDay = hasMonthData && dayStatus === "fully_booked";
                                const isFullDayWaitlist = waitlistEnabled && isFutureDay && isStaffOff;
                                const isFullyBooked = isFutureDay && isFullyBookedDay;
                                const isWaitlistDay =
                                  waitlistEnabled && isFutureDay && isWaitlistCalendarStatus(dayStatus);
                                const isPolicyDay =
                                  !isPast && hasMonthData && availMeta && dayStatus === "booking_policy";
                                const isAvailableDay =
                                  !isPast &&
                                  (monthAvailLoading || !hasMonthData ? true : Boolean(availMeta?.available));
                                const monthFailed =
                                  Boolean(monthAvailError && !monthAvailLoading && monthAvailDays === null);
                                const dayDisabled =
                                  isPast ||
                                  monthAvailLoading ||
                                  monthFailed ||
                                  isSalonClosed ||
                                  (isStaffOff && !isFullDayWaitlist) ||
                                  (isFullyBookedDay && !isWaitlistDay);

                                let dayStateClass = "is-available";
                                if (isPast) dayStateClass = "is-past";
                                else if (isWaitlistDay) dayStateClass = "is-waitlist";
                                else if (isSalonClosed || isFullyBooked || isFullDayWaitlist) dayStateClass = "is-booked";
                                else if (isPolicyDay) dayStateClass = "is-available is-policy-hint";
                                else if (!isAvailableDay) dayStateClass = "is-booked";

                                return (
                                  <button
                                    key={cell.key}
                                    type="button"
                                    disabled={dayDisabled}
                                    className={`cp-edit-appt-review-cal-day ${isSel ? "is-selected" : ""} ${
                                      isToday ? "is-today" : ""
                                    } ${dayStateClass}`}
                                    onClick={() => {
                                      if (dayDateStr !== pickedDateStr) setPickedSlot(null);
                                      setPickedDateStr(dayDateStr);
                                      if (waitlistEnabled && isFullDayWaitlist) {
                                        openWaitlistForDate(dayDateStr, { isFullDay: true });
                                      }
                                    }}
                                  >
                                    {isWaitlistDay ? (
                                      <span className="cp-edit-appt-review-cal-waitlist">
                                        <span>{cell.day}</span>
                                        <small>waitlist</small>
                                      </span>
                                    ) : (
                                      cell.day
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                            <div className="cp-edit-appt-review-cal-legend">
                              <span className="cp-edit-appt-review-cal-legend-item">
                                <span className="cp-edit-appt-review-legend-dot is-avail" aria-hidden />
                                Available
                              </span>
                              <span className="cp-edit-appt-review-cal-legend-item">
                                <span className="cp-edit-appt-review-legend-dot is-today" aria-hidden />
                                Today
                              </span>
                              {bookingPolicyMinutes > 0 ? (
                                <span className="cp-edit-appt-review-cal-legend-item">
                                  <span className="cp-edit-appt-review-legend-dot is-policy-hint" aria-hidden />
                                  Staff available (advance notice for times)
                                </span>
                              ) : null}
                              {waitlistEnabled ? (
                                <span className="cp-edit-appt-review-cal-legend-item">
                                  <span className="cp-edit-appt-review-legend-dot is-waitlist" aria-hidden />
                                  Waitlist
                                </span>
                              ) : (
                                <span className="cp-edit-appt-review-cal-legend-item">
                                  <span className="cp-edit-appt-review-legend-dot is-booked" aria-hidden />
                                  Fully booked
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="cp-edit-appt-review-slots-wrap">
                            <div className="cp-edit-appt-review-slots-head">
                              <span>Choose a new time</span>
                            </div>
                            {waitlistEnabled && pickedDayIsFullDayWaitlist ? (
                              <div className="cp-edit-appt-waitlist-panel">
                                <div className="cp-edit-appt-waitlist-title">
                                  {moment(pickedDateStr, "YYYY-MM-DD").format("ddd D MMM")} has no staff available.
                                </div>
                                <p className="cp-edit-appt-waitlist-text">
                                  Join the waitlist for this day — you will be notified if a slot opens.
                                </p>
                                <button
                                  type="button"
                                  className="cp-edit-appt-waitlist-btn"
                                  onClick={() => openWaitlistForDate(pickedDateStr, { isFullDay: true })}
                                >
                                  Join waitlist
                                </button>
                              </div>
                            ) : isSalonClosedStatus(pickedDayStatus) ? (
                              <div className="cp-edit-appt-waitlist-panel">
                                <div className="cp-edit-appt-waitlist-title">
                                  {moment(pickedDateStr, "YYYY-MM-DD").format("ddd D MMM")} is closed.
                                </div>
                                <p className="cp-edit-appt-waitlist-text">
                                  The salon is not open this day, so waitlist is not available. Please choose another day.
                                </p>
                              </div>
                            ) : isFullDayWaitlistStatus(pickedDayStatus) ? (
                              <div className="cp-edit-appt-waitlist-panel">
                                <div className="cp-edit-appt-waitlist-title">
                                  {moment(pickedDateStr, "YYYY-MM-DD").format("ddd D MMM")} has no staff available.
                                </div>
                                <p className="cp-edit-appt-waitlist-text">
                                  Waitlist is available for future dates only. Please choose another day.
                                </p>
                              </div>
                            ) : (
                              <>
                                {!newTimeChecking && !newTimeValid && newTimeError ? (
                                  <div className="cp-edit-appt-review-keep-time-alert mb-2" role="alert">
                                    <span className="cp-edit-appt-review-keep-time-alert-icon" aria-hidden>
                                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                        <path
                                          d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                                          stroke="currentColor"
                                          strokeWidth="1.75"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                    </span>
                                    <div className="cp-edit-appt-review-keep-time-alert-body">
                                      <strong className="cp-edit-appt-review-keep-time-alert-title">
                                        Selected time is not available
                                      </strong>
                                      <p className="cp-edit-appt-review-keep-time-alert-message mb-0">{newTimeError}</p>
                                    </div>
                                  </div>
                                ) : null}
                                {!daySlotsLoading && !displayBookableSlots.length && showDayWaitlistCta ? (
                                  <div className="cp-edit-appt-waitlist-panel">
                                    <div className="cp-edit-appt-waitlist-title">
                                      {moment(pickedDateStr, "YYYY-MM-DD").format("ddd D MMM")} has no available times.
                                    </div>
                                    <p className="cp-edit-appt-waitlist-text">
                                      Join the waitlist for this day — you will be notified if a slot opens.
                                    </p>
                                    <button
                                      type="button"
                                      className="cp-edit-appt-waitlist-btn"
                                      onClick={() => openWaitlistForDate(pickedDateStr, { isFullDay: true })}
                                    >
                                      Join waitlist
                                    </button>
                                  </div>
                                ) : (
                                  <div className="cp-edit-appt-review-slots-scroll">
                                    {daySlotsLoading ? (
                                      <p className="cp-edit-appt-review-slots-status small text-muted mb-0 px-2">Loading times…</p>
                                    ) : null}
                                    {daySlotsError ? (
                                      <p className="cp-edit-appt-review-slots-status small text-danger mb-2 px-2">{daySlotsError}</p>
                                    ) : null}
                                    {!daySlotsLoading && !displayBookableSlots.length ? (
                                      <p className="cp-edit-appt-review-slots-status small text-muted mb-0 px-2">
                                        {pickedDayInViewMonth &&
                                        monthAvailDays?.find((x) => x.day === pickedDayInViewMonth)?.status === "booking_policy"
                                          ? "Staff are scheduled this day, but online bookings require advance notice. Please choose a later date or contact the salon."
                                          : "No start times on this day for your selection."}
                                      </p>
                                    ) : null}
                                    <div className="cp-edit-appt-review-slots-grid">
                                      {displayBookableSlots.map((slot) => (
                                        <button
                                          key={slot.label}
                                          type="button"
                                          className={`cp-edit-appt-review-slot-btn ${pickedSlot === slot.label ? "is-selected" : ""}`}
                                          onClick={() => setPickedSlot(slot.label)}
                                        >
                                          {formatSlotRangeFromLabel(slot.label, bookingSlotDurationMinutes)}
                                        </button>
                                      ))}
                                    </div>
                                    {showDayWaitlistCta && displayBookableSlots.length > 0 ? (
                                      <div className="cp-edit-appt-waitlist-panel">
                                        <div className="cp-edit-appt-waitlist-title">
                                          Don&apos;t see a time that works?
                                        </div>
                                        <p className="cp-edit-appt-waitlist-text">
                                          Join the waitlist for this day — you will be notified if a slot opens.
                                        </p>
                                        <button
                                          type="button"
                                          className="cp-edit-appt-waitlist-btn"
                                          onClick={() => openWaitlistForDate(pickedDateStr, { isFullDay: true })}
                                        >
                                          Join waitlist
                                        </button>
                                      </div>
                                    ) : null}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </>
              ) : (
                <p className="text-muted small mb-0">No services selected. Go back to step 1.</p>
              )}
            </div>

            {noteOpen ? (
              <>
                <button
                  type="button"
                  className="cp-edit-appt-review-remove-note"
                  onClick={() => {
                    setNoteOpen(false);
                    setAppointmentNote("");
                  }}
                >
                  + Remove note
                </button>
                <label className="cp-edit-appt-review-note-field">
                  <span className="visually-hidden">Appointment note</span>
                  <textarea
                    className="form-control cp-edit-appt-review-note-input"
                    rows={4}
                    placeholder="Allergies, skin concerns, preferences..."
                    value={appointmentNote}
                    onChange={(e) => setAppointmentNote(e.target.value)}
                  />
                </label>
              </>
            ) : (
              <button type="button" className="cp-edit-appt-review-add-note" onClick={() => setNoteOpen(true)}>
                + Add a note to your appointment
              </button>
            )}

            <div className="cp-edit-appt-review-footer">
              <button
                type="button"
                className="cp-edit-appt-review-back-sm"
                onClick={() => (isRescheduleMode ? navigate("/appointments") : setStep(1))}
              >
                <i className="fa fa-arrow-left" aria-hidden="true" /> Back
              </button>
              {saveError ? (
                <div className="alert alert-warning py-2 mb-2" role="alert">
                  {saveError}
                </div>
              ) : null}
              <button
                type="button"
                className="cp-edit-appt-review-save-wide"
                disabled={
                  bookingLines.length === 0 ||
                  saving ||
                  keepTimeChecking ||
                  newTimeChecking ||
                  (!isBookMode && !isRescheduleMode && timeChoice === "keep" && !keepTimeValid) ||
                  ((timeChoice === "new" || isRescheduleMode) && (!pickedSlot || !newTimeValid))
                }
                onClick={handleSaveChanges}
              >
                <span className="cp-edit-appt-review-save-label">
                  {saving
                    ? isBookMode
                      ? "Booking…"
                      : "Saving…"
                    : isRescheduleMode
                      ? "Confirm reschedule"
                      : isBookMode
                        ? "Book appointment"
                        : "Save changes"}
                </span>
                <span className="cp-edit-appt-review-save-price">{formatMoney(lineTotal)}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <BookingWaitlistModal
        show={showWaitlistModal}
        context={waitlistContext}
        salonId={portalSalonId}
        socialLoginEnabled={socialLoginEnabled}
        onClose={() => {
          setShowWaitlistModal(false);
          setWaitlistContext(null);
        }}
      />
    </div>
  );
};

export default CustomerPortalEditAppointment;
